import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Check, Sparkles, Crown, Zap, Loader2, Star, Plane } from 'lucide-react';
import { SwoopyWordmark } from '../components/SwoopyLogo';
import { useUser } from '../context/UserContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { type: 'spring', stiffness: 200, damping: 20 }
  }
};

export default function PricingPage({ onBack }) {
  const { userId } = useUser();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await fetch(`${API_URL}/api/payments/packages`);
      const data = await response.json();
      if (data.packages) {
        setPackages(data.packages);
      }
    } catch (err) {
      console.error('Failed to fetch packages:', err);
    } finally {
      setPageLoading(false);
    }
  };

  const handleBack = () => {
    window.history.pushState({}, '', '/');
    if (onBack) onBack();
  };

  const handleCheckout = async (packageId) => {
    setLoading(packageId);
    setError(null);

    try {
      const originUrl = window.location.origin;
      
      const response = await fetch(`${API_URL}/api/payments/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          package_id: packageId,
          user_id: userId,
          origin_url: originUrl
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Gagal membuat sesi pembayaran');
      }

      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error('URL pembayaran tidak diterima');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(err.message);
      setLoading(null);
    }
  };

  // Default plans with IDR pricing
  const defaultPlans = [
    {
      id: 'basic',
      name: 'Swoopy Basic',
      price: 49000,
      price_display: 'Rp49.000',
      currency: 'IDR',
      description: 'Unlock one premium AI travel itinerary.',
      features: [
        'Premium itinerary unlock',
        'Limited AI recommendations',
        'Downloadable travel summary'
      ],
      button_text: 'Unlock Basic',
      popular: false
    },
    {
      id: 'plus',
      name: 'Swoopy Plus',
      price: 99000,
      price_display: 'Rp99.000',
      currency: 'IDR',
      description: 'Unlock advanced AI travel recommendations and premium itinerary access.',
      features: [
        'Full premium itinerary unlock',
        'Advanced AI travel recommendations',
        'Unlimited premium trip generation',
        'Smart hidden destination suggestions',
        'Premium export access'
      ],
      button_text: 'Unlock Plus',
      popular: true
    }
  ];

  const plans = packages.length > 0 ? packages : defaultPlans;

  // Format price to IDR display
  const formatPrice = (plan) => {
    if (plan.price_display) return plan.price_display;
    return `Rp${plan.price.toLocaleString('id-ID')}`;
  };

  return (
    <div 
      className="min-h-screen pb-12"
      style={{ 
        background: 'linear-gradient(180deg, var(--clay-white) 0%, #FFF5F7 50%, var(--clay-white) 100%)'
      }}
    >
      {/* Header */}
      <header 
        className="sticky top-0 z-50 px-5 py-4"
        style={{
          backgroundColor: 'rgba(248, 248, 248, 0.85)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(233, 116, 143, 0.1)'
        }}
      >
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <motion.button 
            onClick={handleBack}
            className="flex items-center gap-2 p-2.5 -ml-2 rounded-xl"
            style={{ color: 'var(--text-secondary)' }}
            whileTap={{ scale: 0.95 }}
            whileHover={{ backgroundColor: 'var(--swoopy-pink-soft)' }}
            data-testid="pricing-back-btn"
          >
            <ArrowLeft size={20} strokeWidth={2.5} />
            <span className="text-sm font-medium">Kembali</span>
          </motion.button>
          <SwoopyWordmark size="md" />
          <div style={{ width: 70 }} />
        </div>
      </header>

      <div className="max-w-lg mx-auto px-5 pt-10">
        {/* Hero Section */}
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Floating Icon */}
          <motion.div
            className="relative w-24 h-24 mx-auto mb-6"
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          >
            <div 
              className="absolute inset-0 rounded-3xl"
              style={{ 
                background: 'linear-gradient(145deg, #FFB6C1 0%, var(--swoopy-pink) 50%, #FF7B93 100%)',
                boxShadow: '0 20px 40px -10px rgba(233, 116, 143, 0.4), 0 0 0 1px rgba(255,255,255,0.2) inset',
                transform: 'rotate(-6deg)'
              }}
            />
            <div 
              className="absolute inset-0 flex items-center justify-center rounded-3xl"
              style={{ 
                background: 'linear-gradient(145deg, var(--swoopy-pink-light) 0%, var(--swoopy-pink) 100%)',
                boxShadow: '0 15px 35px -8px rgba(233, 116, 143, 0.5)'
              }}
            >
              <Plane size={40} color="white" strokeWidth={2} />
            </div>
          </motion.div>

          <h1 
            className="text-3xl font-semibold mb-3"
            style={{ 
              color: 'var(--text-primary)', 
              fontFamily: "'Fredoka', system-ui",
              letterSpacing: '-0.01em'
            }}
          >
            Unlock Premium
          </h1>
          <p 
            className="text-base max-w-xs mx-auto"
            style={{ color: 'var(--text-muted)', lineHeight: '1.6' }}
          >
            Dapatkan akses penuh ke rekomendasi perjalanan AI
          </p>
        </motion.div>

        {/* Error Message */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              className="mb-8 p-4 rounded-2xl text-center"
              style={{ 
                backgroundColor: '#FEE2E2',
                color: '#DC2626',
                boxShadow: '0 4px 12px rgba(220, 38, 38, 0.1)'
              }}
            >
              {error}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Pricing Cards */}
        {pageLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 size={32} className="animate-spin" style={{ color: 'var(--swoopy-pink)' }} />
          </div>
        ) : (
          <motion.div 
            className="space-y-5"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {plans.map((plan, index) => {
              const isPlus = plan.id === 'plus';
              const isLoading = loading === plan.id;
              
              return (
                <motion.div
                  key={plan.id}
                  variants={cardVariants}
                  className="relative"
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '28px',
                    boxShadow: isPlus 
                      ? '0 25px 50px -12px rgba(233, 116, 143, 0.25), 0 0 0 2px var(--swoopy-pink)'
                      : '0 10px 30px -5px rgba(0, 0, 0, 0.08), 0 0 0 1px var(--border-light)',
                    overflow: 'hidden',
                    transform: isPlus ? 'scale(1.02)' : 'scale(1)',
                  }}
                  whileHover={{ 
                    y: -4, 
                    boxShadow: isPlus 
                      ? '0 30px 60px -15px rgba(233, 116, 143, 0.3), 0 0 0 2px var(--swoopy-pink)'
                      : '0 15px 40px -8px rgba(0, 0, 0, 0.12), 0 0 0 1px var(--border-light)'
                  }}
                  transition={{ duration: 0.2 }}
                  data-testid={`pricing-card-${plan.id}`}
                >
                  {/* Popular Badge */}
                  {isPlus && (
                    <div 
                      className="absolute top-0 right-0 px-5 py-2 flex items-center gap-1.5"
                      style={{
                        background: 'linear-gradient(135deg, var(--swoopy-pink) 0%, #FF7B93 100%)',
                        borderBottomLeftRadius: '16px',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: '600',
                        letterSpacing: '0.02em'
                      }}
                    >
                      <Star size={12} fill="white" />
                      Paling Populer
                    </div>
                  )}

                  {/* Card Content */}
                  <div className="p-6 sm:p-7">
                    {/* Plan Header */}
                    <div className="flex items-start gap-4 mb-5">
                      <div 
                        className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                        style={{ 
                          background: isPlus 
                            ? 'linear-gradient(145deg, var(--swoopy-pink-light) 0%, var(--swoopy-pink) 100%)'
                            : 'linear-gradient(145deg, #FFF0F3 0%, #FFE4E9 100%)',
                          boxShadow: isPlus 
                            ? '0 8px 20px -4px rgba(233, 116, 143, 0.4)'
                            : '0 4px 12px -2px rgba(233, 116, 143, 0.15)'
                        }}
                      >
                        {isPlus ? (
                          <Crown size={24} color="white" strokeWidth={2} />
                        ) : (
                          <Zap size={24} style={{ color: 'var(--swoopy-pink)' }} strokeWidth={2} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 
                          className="font-semibold text-lg mb-1"
                          style={{ 
                            color: 'var(--text-primary)', 
                            fontFamily: "'Fredoka', system-ui" 
                          }}
                        >
                          {plan.name}
                        </h3>
                        <p 
                          className="text-sm leading-relaxed"
                          style={{ color: 'var(--text-muted)' }}
                        >
                          {plan.description}
                        </p>
                      </div>
                    </div>

                    {/* Price */}
                    <div className="mb-6">
                      <div className="flex items-baseline gap-1">
                        <span 
                          className="text-3xl sm:text-4xl font-bold"
                          style={{ 
                            color: isPlus ? 'var(--swoopy-pink)' : 'var(--text-primary)',
                            fontFamily: "'Fredoka', system-ui" 
                          }}
                        >
                          {formatPrice(plan)}
                        </span>
                        <span 
                          className="text-sm font-medium"
                          style={{ color: 'var(--text-muted)' }}
                        >
                          sekali bayar
                        </span>
                      </div>
                    </div>

                    {/* Features */}
                    <ul className="space-y-3 mb-7">
                      {plan.features.map((feature, i) => (
                        <motion.li 
                          key={i} 
                          className="flex items-start gap-3"
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.3 + i * 0.05 }}
                        >
                          <div 
                            className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                            style={{ 
                              background: isPlus 
                                ? 'linear-gradient(145deg, var(--swoopy-pink-light), var(--swoopy-pink))'
                                : 'var(--swoopy-pink-soft)'
                            }}
                          >
                            <Check 
                              size={12} 
                              color={isPlus ? 'white' : 'var(--swoopy-pink)'}
                              strokeWidth={3}
                            />
                          </div>
                          <span 
                            className="text-sm"
                            style={{ 
                              color: 'var(--text-secondary)',
                              lineHeight: '1.5'
                            }}
                          >
                            {feature}
                          </span>
                        </motion.li>
                      ))}
                    </ul>

                    {/* CTA Button */}
                    <motion.button
                      onClick={() => handleCheckout(plan.id)}
                      disabled={loading !== null}
                      className="w-full h-14 rounded-2xl text-base font-semibold transition-all relative overflow-hidden"
                      style={{
                        background: isPlus 
                          ? 'linear-gradient(145deg, #FFB6C1 0%, var(--swoopy-pink) 30%, #FF7B93 100%)'
                          : 'white',
                        color: isPlus ? 'white' : 'var(--swoopy-pink)',
                        border: isPlus ? 'none' : '2px solid var(--swoopy-pink)',
                        boxShadow: isPlus 
                          ? '0 10px 25px -5px rgba(233, 116, 143, 0.4)'
                          : 'none',
                        opacity: loading !== null && !isLoading ? 0.5 : 1,
                        fontFamily: "'Fredoka', system-ui"
                      }}
                      whileTap={{ scale: loading ? 1 : 0.98 }}
                      whileHover={{ 
                        scale: loading ? 1 : 1.02,
                        boxShadow: isPlus 
                          ? '0 15px 35px -8px rgba(233, 116, 143, 0.5)'
                          : '0 5px 15px -3px rgba(233, 116, 143, 0.2)'
                      }}
                      data-testid={`checkout-${plan.id}-btn`}
                    >
                      {/* Button shine effect */}
                      {isPlus && (
                        <div 
                          className="absolute top-0 left-0 right-0 h-1/2 pointer-events-none"
                          style={{ 
                            background: 'linear-gradient(180deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0) 100%)'
                          }}
                        />
                      )}
                      <span className="relative z-10 flex items-center justify-center gap-2">
                        {isLoading ? (
                          <>
                            <Loader2 size={20} className="animate-spin" />
                            Memproses...
                          </>
                        ) : (
                          <>
                            {isPlus && <Sparkles size={18} />}
                            {plan.button_text}
                          </>
                        )}
                      </span>
                    </motion.button>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}

        {/* Payment Note */}
        <motion.div 
          className="mt-10 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          {/* IDR Payment Note */}
          <p 
            className="text-sm mb-4 font-medium"
            style={{ color: 'var(--swoopy-pink)' }}
          >
            Pembayaran diproses dalam Rupiah (IDR).
          </p>

          {/* Trust Badges */}
          <div className="flex items-center justify-center gap-6 mb-4">
            <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-muted)' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0110 0v4"/>
              </svg>
              Pembayaran aman
            </div>
            <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-muted)' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
              SSL terenkripsi
            </div>
          </div>
          <p 
            className="text-xs"
            style={{ color: 'var(--text-muted)' }}
          >
            Transaksi aman • Akses selamanya
          </p>
        </motion.div>
      </div>
    </div>
  );
}
